import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import AIGender from '../enums/ai/AIGender';
import AITon from '../enums/ai/AITon';
import { AIStatus } from '@leedpulse-monorepo/enums';
import { IAI } from '@leedpulse-monorepo/interfaces';

export default class AI {
  private _id: string;
  private _action: string;
  private _behaviour: string;
  private _constraints: string;
  private _created: Date;
  private _updated: Date;
  private _domain: string; // NOTE : This has become a domainId
  private _fullname: string;
  private _gender: AIGender;
  private _idApiKey: string;
  private _intro: string;
  private _location: string;
  private _name: string;
  private _status: AIStatus;
  private _profile: string;
  private _replyTiming: number[];
  private _reminderTiming: number[];
  private _flowTiming: number;
  private _target: string;
  private _ton: AITon;
  private _website: string;
  private _aiReceptionist?: IAI['ai_receptionist'];

  constructor(
    id: string,
    action: string,
    behaviour: string,
    constraints: string,
    created: Date,
    updated: Date,
    domain: string, // NOTE : This has become a domainId
    fullname: string,
    gender: AIGender,
    idApiKey: string,
    intro: string,
    location: string,
    name: string,
    status: AIStatus,
    profile: string,
    replyTiming: number[],
    reminderTiming: number[],
    flowTiming: number,
    target: string,
    ton: AITon,
    website: string,
    aiReceptionist?: IAI['ai_receptionist']
  ) {
    this._id = id;
    this._action = action;
    this._behaviour = behaviour;
    this._constraints = constraints;
    this._created = created;
    this._updated = updated;
    this._domain = domain;
    this._fullname = fullname;
    this._gender = gender;
    this._idApiKey = idApiKey;
    this._intro = intro;
    this._location = location;
    this._name = name;
    this._status = status;
    this._profile = profile;
    this._replyTiming = replyTiming;
    this._reminderTiming = reminderTiming;
    this._flowTiming = flowTiming;
    this._target = target;
    this._ton = ton;
    this._website = website;
    this._aiReceptionist = aiReceptionist;
  }

  get id(): string {
    return this._id;
  }

  get action(): string {
    return this._action;
  }

  get behaviour(): string {
    return this._behaviour;
  }

  get constraints(): string {
    return this._constraints;
  }

  get created(): Date {
    return this._created;
  }

  get updated(): Date {
    return this._updated;
  }

  get domain(): string {
    return this._domain;
  }

  get fullname(): string {
    return this._fullname;
  }

  get gender(): AIGender {
    return this._gender;
  }

  get idApiKey(): string {
    return this._idApiKey;
  }

  get intro(): string {
    return this._intro;
  }

  get location(): string {
    return this._location;
  }

  get name(): string {
    return this._name;
  }

  get status(): AIStatus {
    return this._status;
  }

  get profile(): string {
    return this._profile;
  }

  get replyTiming(): number[] {
    return this._replyTiming;
  }

  get reminderTiming(): number[] {
    return this._reminderTiming;
  }

  get flowTiming(): number {
    return this._flowTiming;
  }

  get target(): string {
    return this._target;
  }

  get ton(): AITon {
    return this._ton;
  }

  get website(): string {
    return this._website;
  }

  get aiReceptionist(): IAI['ai_receptionist'] {
    return this._aiReceptionist;
  }

  public copyWith({
    action,
    behaviour,
    constraints,
    domain,
    fullname,
    gender,
    intro,
    location,
    name,
    status,
    profile,
    replyTiming,
    reminderTiming,
    flowTiming,
    target,
    ton,
    website,
    aiReceptionist,
  }: {
    action?: string;
    behaviour?: string;
    constraints?: string;
    domain?: string;
    fullname?: string;
    gender?: AIGender;
    intro?: string;
    location?: string;
    name?: string;
    status?: AIStatus;
    profile?: string;
    replyTiming?: number[];
    reminderTiming?: number[];
    flowTiming?: number;
    target?: string;
    ton?: AITon;
    website?: string;
    aiReceptionist?: IAI['ai_receptionist'];
  }): AI {
    return new AI(
      this._id,
      action ?? this._action,
      behaviour ?? this._behaviour,
      constraints ?? this._constraints,
      this._created,
      new Date(),
      domain ?? this._domain,
      fullname ?? this._fullname,
      gender ?? this._gender,
      this._idApiKey,
      intro ?? this._intro,
      location ?? this._location,
      name ?? this._name,
      status ?? this._status,
      profile ?? this._profile,
      replyTiming ?? this._replyTiming,
      reminderTiming ?? this._reminderTiming,
      flowTiming ?? this._flowTiming,
      target ?? this._target,
      ton ?? this._ton,
      website ?? this._website,
      aiReceptionist ?? this._aiReceptionist
    );
  }

  // Activities and motifs are ids of the domain's catalog: they do not survive a domain change,
  // and going back to the saved domain gives its saved ones back
  public withDomain(domain: string, saved?: AI): AI {
    if (domain === this._domain) return this;
    const isSavedDomain = domain === saved?._domain;
    return this.copyWith({
      domain,
      aiReceptionist: {
        ...this._aiReceptionist,
        activities: isSavedDomain ? saved._aiReceptionist?.activities ?? [] : [],
        motifs: isSavedDomain ? saved._aiReceptionist?.motifs ?? [] : [],
      },
    });
  }

  static fromFirestore(
    doc: QueryDocumentSnapshot<DocumentData, DocumentData>
  ): AI {
    const docData = doc.data();

    return new AI(
      doc.id,
      docData.action,
      docData.behaviour,
      docData.constraints,
      docData.created,
      docData.updated,
      docData.domain,
      docData.fullname,
      docData.gender,
      docData.id_api_key,
      docData.intro,
      docData.location,
      docData.name,
      docData.status,
      docData.profile,
      docData.reply_timing,
      docData.reminder_timing,
      docData.flow_timing,
      docData.target,
      docData.ton,
      docData.website,
      docData.ai_receptionist ?? {
        first_message: '',
        qualification_criteria: '',
        voice_id: '',
        language: '',
        personality: '',
        environment: '',
        tone: '',
        goal: '',
        closing: '',
        guardrails: '',
        other: '',
        activities: [],
        motifs: [],
      }
    );
  }

  toFirestorePatch(): Record<string, any> {
    const now = new Date();

    const patch: Record<string, any> = {
      updated: now.getTime(),
      action: this._action,
      behaviour: this._behaviour,
      constraints: this._constraints,
      domain: this._domain,
      fullname: this._fullname,
      gender: this._gender,
      intro: this._intro,
      location: this._location,
      name: this._name,
      status: this._status,
      profile: this._profile,
      reply_timing: this._replyTiming,
      reminder_timing: this._reminderTiming,
      flow_timing: this._flowTiming,
      target: this._target,
      ton: this._ton,
      website: this._website,
    };

    const aiReceptionistPatch: Record<string, any> = {
      'ai_receptionist.first_message': this._aiReceptionist?.first_message,
      'ai_receptionist.qualification_criteria': this._aiReceptionist?.qualification_criteria,
      'ai_receptionist.voice_id': this._aiReceptionist?.voice_id,
      'ai_receptionist.language': this._aiReceptionist?.language,
      'ai_receptionist.personality': this._aiReceptionist?.personality,
      'ai_receptionist.environment': this._aiReceptionist?.environment,
      'ai_receptionist.tone': this._aiReceptionist?.tone,
      'ai_receptionist.goal': this._aiReceptionist?.goal,
      'ai_receptionist.closing': this._aiReceptionist?.closing,
      'ai_receptionist.guardrails': this._aiReceptionist?.guardrails,
      'ai_receptionist.other': this._aiReceptionist?.other,
      'ai_receptionist.activities': this._aiReceptionist?.activities,
      'ai_receptionist.motifs': this._aiReceptionist?.motifs,
    };

    for (const [key, value] of Object.entries(aiReceptionistPatch)) {
      if (value !== undefined) {
        patch[key] = value;
      }
    }

    return patch;
  }
}
